import { Checkbox, Icon, Spinner, TextField as ACPaaSTextField } from '@acpaas-ui/react-components';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { ALL_SELECT_OPTION } from '../../constants/settings.constants';
import { useOutsideClick } from '../../hooks/useOutsideClick.hook';
import Flyout from '../core/Flyout';
import { INPUT_STATES } from './forms.utils';
import TextField from './TextField';

// IF used in a scrollable container pass this container HTML element in
// parentElement prop to allow smooth positioning of the flyout

// Needs to be set to flyoutRelative is height of the container will change when its open
const SelectField = ({
  label,
  name,
  placeholder = 'Selecteer',
  onChange,
  onClose = _.noop,
  onOpen = _.noop,
  required,
  disabled,
  disableSearch,
  isSubmitting,
  value,
  multiple,
  allOption,
  className = '',
  description = '',
  hasError,
  hasWarning,
  validationError,
  state,
  flyoutDirection,
  flyoutRelative,
  flyoutSize,
  parentElement,
  readOnly,
  options = []
}) => {
  const id = useRef(uuid());
  const listItemRefs = useRef({});
  const flyoutRootRef = useRef(null);
  const inputRef = useRef(null);
  const searchInputRef = useRef(null);
  const selectWrapperRef = useRef(null);
  const [shouldFocusSearch, setShouldFocusSearch] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const isConnected = !!state;

  const filteredOptions = options?.filter((opt) => opt.label?.toLowerCase()?.includes(search?.toLowerCase()));
  const sortedOptions = [allOption ? ALL_SELECT_OPTION : null, ..._.sortBy(filteredOptions, (opt) => opt.label)].filter(
    (opt) => !!opt
  );

  const { elRef } = useOutsideClick((clickedElement) => {
    if (disabled || readOnly) {
      return;
    }

    setSearch('');
    if (clickedElement === selectWrapperRef.current || clickedElement === inputRef.current) {
      return;
    }
    setIsOpen(false);
    onClose(value);
  });

  const handleItemSelect = (event, itemValue) => {
    event.stopPropagation();
    if (disabled || readOnly) {
      return;
    }

    let newValue;
    if (multiple && itemValue === ALL_SELECT_OPTION.value) {
      newValue = value.includes(itemValue) ? [] : [itemValue];
    } else if (multiple) {
      newValue = value.includes(itemValue) ? value.filter((v) => v !== itemValue) : [...value, itemValue];
      newValue = newValue.filter((value) => value !== ALL_SELECT_OPTION.value);
    } else {
      newValue = value === itemValue ? '' : itemValue;
    }
    onChange(newValue);
    if (!multiple) {
      onClose && onClose(newValue);
      setIsOpen(false);
    }
  };

  const handleClick = (e) => {
    if (disabled || readOnly) {
      return;
    }

    if (!isOpen) {
      setShouldFocusSearch(true);
      setIsOpen(true);
      onOpen();
    } else {
      setIsOpen(false);
      onClose(value);
    }
  };

  useEffect(() => {
    if (shouldFocusSearch && searchInputRef.current) {
      searchInputRef?.current?.focus();
      setShouldFocusSearch(false);
    }
  }, [shouldFocusSearch]);

  const valueString = multiple
    ? [ALL_SELECT_OPTION, ...options]
        .filter((opt) => value.includes(opt.value))
        ?.map((opt) => opt.label)
        ?.join(', ')
    : [ALL_SELECT_OPTION, ...options].find((opt) => value === opt.value)?.label ?? '';

  if (readOnly) {
    return <ACPaaSTextField className={className} readOnly value={valueString} label={label} />;
  }

  return (
    <div
      ref={selectWrapperRef}
      className={`spo-a-select ${disabled ? 'is-disabled' : ''} ${isConnected ? 'connected' : ''} ${
        isOpen ? 'is-open' : ''
      }`}
      onClick={handleClick}
    >
      <div
        className={`a-input has-icon-right ${required ? 'is-required' : ''} ${
          hasError || !!validationError ? 'a-input--error' : ''
        } ${hasWarning ? 'a-input--warning' : ''} ${className}`}
        name={`select-${name}`}
      >
        <div className="a-input__wrapper">
          {label && (
            <label className="a-input__label" htmlFor={`select-${id.current}`}>
              {label}
            </label>
          )}
          <div
            className={`spo-a-select__value ${!valueString && placeholder ? 'u-text-light' : ''}`}
            ref={inputRef}
            title={valueString}
            name={`select-${id.current}`}
          >
            {valueString || placeholder}
          </div>
          <div ref={flyoutRootRef}></div>
          {description && !validationError && !hasError && !hasWarning && <small>{description}</small>}
          {(!!validationError || hasError) && <small className="u-text-danger">{validationError || description}</small>}
          {!hasError && hasWarning && <small className="u-text-warning">{description}</small>}
          {isSubmitting ? (
            <Spinner />
          ) : (
            <Icon
              name={`${
                hasError || !!validationError
                  ? 'ai-alert-triangle'
                  : hasWarning && !isOpen
                  ? 'ai-alert-circle'
                  : isOpen
                  ? 'ai-arrow-up-1'
                  : 'ai-arrow-down-1'
              }`}
            />
          )}
        </div>
      </div>
      <Flyout
        rootElement={flyoutRootRef.current}
        parentElement={parentElement}
        direction={flyoutDirection}
        isOpen={isOpen}
        relative={flyoutRelative}
        size={flyoutSize}
      >
        <div ref={elRef}>
          {!disableSearch && (
            <li
              className="m-selectable-list__item search"
              onClick={(e) => {
                e.stopPropagation();
                searchInputRef?.current?.focus();
              }}
            >
              <TextField
                ref={searchInputRef}
                state={INPUT_STATES.DEFAULT}
                value={search}
                placeholder="Zoeken"
                onChange={(value) => setSearch(value)}
              />
              <Icon className="u-text-light" name="ai-search" />
            </li>
          )}
          <ul className="m-selectable-list">
            {sortedOptions.map((opt) => (
              <li
                ref={(el) => (listItemRefs.current[opt.value] = el)}
                className={`m-selectable-list__item ${
                  multiple ? value.includes(opt.value) : value === opt.value ? 'm-selectable-list__item--active' : ''
                }`}
                key={opt.value}
                onClick={(e) => handleItemSelect(e, opt.value)}
                title={opt.label}
              >
                {multiple ? (
                  <div className="u-flex u-flex-align-center">
                    <Checkbox
                      // label={<span>{opt.label}</span>}
                      checked={multiple ? value?.includes(opt.value) ?? false : value === opt.value}
                    />
                    <span className="u-overflow-hidden u-text-overflow-ellipsis u-margin-right-xs">{opt.label}</span>
                  </div>
                ) : (
                  <div className="single-select">{opt.label}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </Flyout>
    </div>
  );
};

export default SelectField;
